
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Note {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  lesson_id?: string;
  module_id?: string;
  course_id: string;
  user_id: string;
  courses?: {
    title: string;
  };
  lessons?: {
    title: string;
  };
  modules?: {
    title: string;
  };
}

interface SaveNoteParams {
  courseId: string;
  content: string;
  lessonId?: string;
  moduleId?: string;
}

export const useStudentNotes = (courseId?: string, lessonId?: string, moduleId?: string) => {
  const queryClient = useQueryClient();
  
  // Get current note
  const {
    data: note,
    isLoading,
    error,
  } = useQuery<Note | null>({
    queryKey: ['student-note', courseId, lessonId, moduleId],
    queryFn: async () => {
      if (!courseId || (!lessonId && !moduleId)) {
        return null;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      let query = supabase
        .from('student_notes')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId);
        
      if (lessonId) {
        query = query.eq('lesson_id', lessonId);
      } else if (moduleId) {
        query = query.eq('module_id', moduleId);
      }
      
      const { data, error } = await query.maybeSingle();
      
      if (error) {
        console.error("Error fetching note:", error);
        return null;
      }
      
      return data as Note | null;
    },
    enabled: !!courseId && (!!lessonId || !!moduleId),
    staleTime: 5 * 60_000,        // 5 minutes
    gcTime: 30 * 60_000,          // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  
  // Save or update note
  const saveNote = useMutation({
    mutationFn: async ({ courseId, content, lessonId, moduleId }: SaveNoteParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      // If note exists, update it
      if (note?.id) {
        const { data, error } = await supabase
          .from('student_notes')
          .update({ 
            content,
            updated_at: new Date().toISOString()
          })
          .eq('id', note.id)
          .select()
          .single();
          
        if (error) throw error;
        return data;
      }
      
      // Otherwise, create a new note
      const { data, error } = await supabase
        .from('student_notes')
        .insert({
          user_id: user.id,
          course_id: courseId,
          lesson_id: lessonId || null,
          module_id: moduleId || null,
          content,
        })
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-note', courseId, lessonId, moduleId] });
      queryClient.invalidateQueries({ queryKey: ['all-student-notes'] });
    },
    onError: (error) => {
      console.error("Error saving note:", error);
      toast.error("Failed to save note");
    }
  });
  
  // Get all notes for a user - improved to avoid duplications
  const getAllNotes = (userId?: string) => {
    return useQuery<Note[]>({
      queryKey: ['all-student-notes', userId],
      queryFn: async () => {
        console.log("Fetching all notes");
        if (!userId) {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return [];
          userId = user.id;
        }
        
        const { data, error } = await supabase
          .from('student_notes')
          .select(`
            *,
            courses(title),
            lessons(title),
            modules(title)
          `)
          .eq('user_id', userId)
          .order('updated_at', { ascending: false });
          
        if (error) {
          console.error("Error fetching all notes:", error);
          return [];
        }
        
        console.log(`Fetched ${data?.length || 0} notes`);
        return data as Note[];
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30,   // 30 minutes
      refetchOnMount: false,     // Don't refetch on mount
      refetchOnWindowFocus: false, // Don't refetch on window focus
      enabled: !!userId,
    });
  };
  
  return {
    note,
    isLoading,
    error,
    saveNote,
    getAllNotes,
  };
};
